import { useSnackbar } from 'notistack';
import { useState, useCallback, useMemo } from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import {
    Box,
    Card,
    List,
    Stack,
    Divider,
    ListItem,
    CardHeader,
    IconButton,
    Pagination,
    Typography,
    CardContent,
    ListItemText
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { ITask, TaskStatus } from 'src/services/tasks/tasks.types';
import { useMarkTaskCompletedMutation } from 'src/services/tasks/tasks.service';

import Iconify from 'src/components/iconify';
import EmptyContent from 'src/components/empty-content';
import DateDisplay from 'src/components/date/date-display';
import TaskPriorityLabel from 'src/components/tasks/task-priority-label';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

import { LoadingCardItem } from 'src/sections/admin/billing/payment/payment-card-item';

// ----------------------------------------------------------------------

type Props = {
    loading?: boolean;
    tasks: ITask[];
    onPageChange?: (page: number) => void;
    onAddTask?: () => void;
    onTaskCompleted?: (taskId: string) => void;
};

type ViewMode = 'detail' | 'list';

const TASKS_PER_PAGE = 5;

export default function PendingTaskManager({
    tasks,
    loading = false,
    onPageChange,
    onAddTask,
    onTaskCompleted,
}: Props) {
    const { t } = useTranslate();
    const { enqueueSnackbar } = useSnackbar();

    const [viewMode, setViewMode] = useState<ViewMode>('detail');
    const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);

    const [markTaskCompleted, { isLoading: isUpdating }] = useMarkTaskCompletedMutation();

    // Filter only pending tasks
    const pendingTasks = tasks.filter(task => task.status === TaskStatus.PENDING);

    const currentTask = useMemo(() => {
        if (pendingTasks.length === 0) return null;
        return pendingTasks[currentTaskIndex];
    }, [pendingTasks, currentTaskIndex]);

    const totalPages = Math.ceil(pendingTasks.length / TASKS_PER_PAGE);

    // Get tasks for current page in list view
    const startIndex = (currentPage - 1) * TASKS_PER_PAGE;
    const endIndex = startIndex + TASKS_PER_PAGE;
    const currentPageTasks = pendingTasks.slice(startIndex, endIndex);

    const handlePreviousTask = useCallback(() => {
        setCurrentTaskIndex((prev) => (prev > 0 ? prev - 1 : pendingTasks.length - 1));
    }, [pendingTasks.length]);

    const handleNextTask = useCallback(() => {
        setCurrentTaskIndex((prev) => (prev < pendingTasks.length - 1 ? prev + 1 : 0));
    }, [pendingTasks.length]);

    const handleMarkComplete = useCallback(async (taskId: string) => {
        try {
            await markTaskCompleted({ taskId }).unwrap();
            enqueueSnackbar(t('tasks.api.complete.success'), { variant: 'success' });

            onTaskCompleted?.(taskId);

            // Move to next task if current task was completed
            if (currentTask?.id === taskId && pendingTasks.length > 1) {
                // handleNextTask();
                setCurrentTaskIndex(0);
            }
        } catch (error) {
            enqueueSnackbar(t('tasks.api.default_error'), { variant: 'error' });
        }
    }, [currentTask?.id, pendingTasks.length, markTaskCompleted, enqueueSnackbar, t, onTaskCompleted]);

    const handleTaskClick = useCallback((taskIndex: number) => {
        setCurrentTaskIndex(taskIndex);
        setViewMode('detail');
    }, []);

    const handlePageChange = useCallback((_event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
    }, []);

    const toggleViewMode = useCallback(() => {
        setViewMode(prev => prev === 'detail' ? 'list' : 'detail');
    }, []);

    if (loading) {
        return (
            <Card>
                <CardHeader
                    title={
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>

                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Iconify icon="mdi:clipboard-check-outline" />
                                <Typography variant="h6">
                                    {t('tasks.pending_manager.title')}
                                </Typography>
                            </Stack>


                        </Stack>
                    }
                />
                <CardContent>
                    <Stack sx={{ width: '100%' }} spacing={2} justifyContent="center" alignItems="center">
                        <LoadingCardItem />
                        <LoadingCardItem />
                        <LoadingCardItem />
                    </Stack>
                </CardContent>
            </Card>
        );
    }

    if (pendingTasks.length === 0) {
        return (
            <Card>
                <CardHeader
                    title={
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Iconify icon="mdi:clipboard-check-outline" />
                            <Typography variant="h6">
                                {t('tasks.pending_manager.title')}
                            </Typography>
                        </Stack>
                    }
                    action={
                        <Stack direction="row">
                            {
                                onAddTask && (
                                    <IconButton
                                        size="small"
                                        onClick={onAddTask}
                                    >
                                        <Iconify icon="eva:plus-fill" />
                                    </IconButton>
                                )
                            }
                        </Stack>
                    }
                />
                <CardContent>
                    <EmptyContent
                        title={t('tasks.pending_manager.no_pending_tasks')}
                        description={t('tasks.pending_manager.no_pending_tasks_description')}
                        sx={{ py: 3 }}
                    />
                </CardContent>
            </Card>
        );
    }

    const renderDetailView = () => {

        if (!currentTask) return null;

        return (
            <CardContent>

                <Box sx={{ mb: 3 }}>
                    <TruncatedTextField
                        text={currentTask.title}
                        mode="lines"
                        limit={2}
                        showMoreEnabled={false}
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                        style={{ textTransform: 'capitalize' }}
                    />

                    {currentTask.due_at && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <Iconify icon="mdi:clock-outline" width={16} height={16} />
                            <DateDisplay date={currentTask.due_at} format="DD MMM YYYY, hh:mm A" variant="body2" />
                        </Stack>
                    )}

                    {currentTask.contact && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Iconify icon="hugeicons:user" width={16} height={16} />
                            <TruncatedTextField
                                text={`${currentTask.contact.first_name} ${currentTask.contact.last_name}`}
                                mode="characters"
                                limit={20}
                                showMoreEnabled={false}
                                variant="body2"
                                style={{ textTransform: 'capitalize' }}
                            />
                        </Stack>
                    )}

                    {currentTask.company_profile && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Iconify icon="uil:building" width={16} height={16} />
                            <TruncatedTextField
                                text={currentTask.company_profile.company_name}
                                mode="characters"
                                limit={20}
                                showMoreEnabled={false}
                                variant="body2"
                                style={{ textTransform: 'capitalize' }}
                            />
                        </Stack>
                    )}

                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Iconify icon="mdi:flag-outline" width={16} height={16} />
                            <TaskPriorityLabel priority={currentTask.priority} />
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Iconify icon="mdi:tag-outline" width={16} height={16} />
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                                {currentTask.type.replace('_', ' ')}
                            </Typography>
                        </Stack>
                    </Stack>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        {t('tasks.viewer.description')}
                    </Typography>
                    <TruncatedTextField
                        text={currentTask.description || t('common.not_set')}
                        mode="lines"
                        limit={3}
                        showMoreEnabled
                        variant="body2"
                        color="text.secondary"
                        style={{ whiteSpace: 'pre-wrap' }}
                    />
                </Box>

                <LoadingButton
                    fullWidth
                    variant="contained"
                    color="primary"
                    loading={isUpdating}
                    onClick={() => handleMarkComplete(currentTask.id)}
                    startIcon={<Iconify icon="mdi:check-circle-outline" />}
                >
                    {t('tasks.actions.complete')}
                </LoadingButton>

                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ width: '100%', flexGrow: 1, mt: 3 }}>
                    <IconButton
                        size="small"
                        onClick={handlePreviousTask}
                        disabled={pendingTasks.length <= 1}
                    >
                        <Iconify icon="eva:arrow-left-fill" />
                    </IconButton>
                    <Typography variant="subtitle2" >
                        {currentTaskIndex + 1} of {pendingTasks.length} tasks
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={handleNextTask}
                        disabled={pendingTasks.length <= 1}
                    >
                        <Iconify icon="eva:arrow-right-fill" />
                    </IconButton>
                </Stack>
            </CardContent>
        );
    }

    const renderListView = () => (
        <CardContent>
            <List sx={{ width: '100%', bgcolor: 'background.paper', p: 0 }}>
                {currentPageTasks.map((task, index) => {
                    const globalIndex = startIndex + index;
                    return (
                        <Box key={task.id}>
                            <ListItem
                                sx={{
                                    py: 1.5,
                                    px: 1,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' }
                                }}
                                onClick={() => handleTaskClick(globalIndex)}
                            >
                                <ListItemText
                                    primary={
                                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                                            <TruncatedTextField
                                                text={task.title}
                                                mode="characters"
                                                limit={30}
                                                showMoreEnabled={false}
                                                variant="subtitle2"
                                                fontWeight="bold"
                                                style={{ textTransform: 'capitalize' }}
                                            />
                                            <TaskPriorityLabel priority={task.priority} />
                                        </Stack>
                                    }
                                    secondary={
                                        <Stack direction="column" spacing={0.5} sx={{ mt: 0.5 }}>
                                            {task.due_at && (
                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                    <Iconify icon="mdi:clock-outline" width={12} height={12} />
                                                    <DateDisplay date={task.due_at} format="DD MMM" variant="caption" />
                                                </Stack>
                                            )}
                                            {(task.contact || task.company_profile) && (
                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                    <Iconify icon="hugeicons:user" width={12} height={12} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {task.contact
                                                            ? `${task.contact.first_name} ${task.contact.last_name}`
                                                            : task.company_profile?.company_name
                                                        }
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </Stack>
                                    }
                                />
                            </ListItem>
                            {index < currentPageTasks.length - 1 && <Divider />}
                        </Box>
                    );
                })}
            </List>

            {totalPages > 1 && (
                <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        size="small"
                    />
                </Stack>
            )}
        </CardContent>
    );

    return (
        <Card>
            <CardHeader
                title={
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Iconify icon="mdi:clipboard-check-outline" />
                        <Typography variant="h6">
                            {t('tasks.pending_manager.title')}
                        </Typography>
                    </Stack>
                }
                action={
                    <Stack direction="row">
                        {
                            onAddTask && (
                                <IconButton
                                    size="small"
                                    onClick={onAddTask}
                                >
                                    <Iconify icon="eva:plus-fill" />
                                </IconButton>
                            )
                        }
                        <IconButton
                            size="small"
                            onClick={toggleViewMode}
                        >
                            <Iconify icon={viewMode === 'detail' ? 'mdi:view-list' : 'mdi:view-dashboard'} />
                        </IconButton>
                    </Stack>
                }
            />

            {viewMode === 'detail' ? renderDetailView() : renderListView()}

        </Card>
    );
}